import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Linkedin, ArrowLeft } from "lucide-react";
import { api } from "@/api";
import Reveal from "@/components/Reveal";
import Portrait from "@/components/Portrait";
import NotFound from "@/pages/NotFound";

function Block({ title, value, testid }) {
  if (!value) return null;
  return (
    <div data-testid={testid} className="border-b border-navy-900/10 pb-8">
      <h2 className="eyebrow">{title}</h2>
      <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-600">{value}</p>
    </div>
  );
}

export default function LeaderProfile() {
  const { slug } = useParams();
  const [leader, setLeader] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setLeader(null);
    setMissing(false);
    api.get(`/leadership/by-slug/${slug}`)
      .then((r) => setLeader(r.data))
      .catch(() => setMissing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (missing) return <NotFound />;
  if (!leader) return <div className="min-h-screen bg-navy-950" data-testid="leader-loading" />;

  return (
    <div data-testid={`leader-profile-${slug}`}>
      <section className="bg-navy-950 pb-24 pt-40 lg:pt-48" data-testid="leader-hero">
        <div className="container-rcs">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-10 flex items-center gap-2 text-xs text-slate-400" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-gold">Home</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <Link to="/leadership" className="transition-colors hover:text-gold">Leadership</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <span className="text-gold">{leader.fullName}</span>
            </nav>
            <div className="grid items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <Portrait name={leader.fullName} src={leader.photograph} size="xl" testid="leader-portrait" />
              </div>
              <div className="lg:col-span-8">
                <h1 className="font-serif text-4xl text-ivory sm:text-5xl">{leader.fullName}</h1>
                <p className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-gold">{leader.designation}</p>
                <p className="mt-1 text-sm text-slate-400">{leader.brand || leader.organisation}</p>
                {leader.shortBiography && (
                  <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300">{leader.shortBiography}</p>
                )}
                {leader.quote && (
                  <blockquote className="mt-8 max-w-2xl border-l-2 border-gold pl-6 font-serif text-xl italic leading-relaxed text-ivory" data-testid="leader-quote">
                    “{leader.quote}”
                  </blockquote>
                )}
                {leader.linkedinURL && (
                  <a
                    href={leader.linkedinURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="leader-linkedin"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-xs font-semibold text-slate-200 transition-colors hover:border-gold hover:text-gold"
                  >
                    <Linkedin className="h-4 w-4" aria-hidden /> LinkedIn Profile
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="leader-details">
        <div className="container-rcs grid gap-16 lg:grid-cols-12">
          <div className="space-y-10 lg:col-span-8">
            <Block title="Introduction" value={leader.fullBiography} testid="leader-bio" />
            <Block title="Professional Background" value={leader.professionalBackground} testid="leader-background" />
            <Block title="Education & Qualifications" value={leader.education} testid="leader-education" />
            <Block title="Areas of Expertise" value={leader.expertise} testid="leader-expertise" />
            <Block title="Leadership Responsibilities" value={leader.responsibilities} testid="leader-responsibilities" />
            <Block title="Leadership Philosophy" value={leader.leadershipPhilosophy} testid="leader-philosophy" />
            <Block title="Achievements" value={leader.achievements} testid="leader-achievements" />
            {!leader.fullBiography && !leader.professionalBackground && !leader.education && !leader.expertise && (
              <p className="rounded-2xl border border-dashed border-navy-900/20 bg-white px-8 py-10 text-sm leading-relaxed text-slate-600" data-testid="leader-profile-pending">
                A detailed profile for {leader.fullName} will be published soon.
              </p>
            )}
          </div>
          <Reveal delay={0.15} className="lg:col-span-4">
            <div className="sticky top-28 rounded-2xl bg-navy-950 p-8" data-testid="leader-sidebar">
              <p className="eyebrow-light">At a Glance</p>
              <dl className="mt-6 space-y-5 text-sm">
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Name</dt>
                  <dd className="mt-1 font-semibold text-ivory">{leader.fullName}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Designation</dt>
                  <dd className="mt-1 font-semibold text-ivory">{leader.designation}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Organisation</dt>
                  <dd className="mt-1 font-semibold text-ivory">{leader.brand || leader.organisation}</dd>
                </div>
              </dl>
              <Link to="/leadership" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold transition-colors hover:text-gold-light" data-testid="leader-back-link">
                <ArrowLeft className="h-4 w-4" aria-hidden /> All Leadership
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
